import type { MaybeRefOrGetter } from 'vue';
import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import { xor } from 'lodash-es';
import { computed, ref, toValue } from 'vue';
import { addServiceAccountToWorkspace, removeServiceAccountFromWorkspace } from '#layers/access/utils/api/service-account-workspaces';
import { useSystemEntityStore } from '#layers/runtime/public';

export function useServiceAccountWorkspaces(
	serviceAccount: MaybeRefOrGetter<ResolvedMonospaceServiceAccount | null>,
	initialWorkspaces: MaybeRefOrGetter<MonospaceWorkspace[]>,
) {
	const startingWorkspaces = toValue(initialWorkspaces);
	const originalWorkspaces = toValue(serviceAccount) ? startingWorkspaces : [];

	const store = useSystemEntityStore();
	const { data: allWorkspaces } = store.MonospaceWorkspace.query((q) => q.many());

	const assignedWorkspaces = ref<MonospaceWorkspace[]>([...startingWorkspaces]);

	const availableWorkspaces = computed<MonospaceWorkspace[]>(() => {
		const modifiedIds = new Set(assignedWorkspaces.value.map((p) => p.id));
		return allWorkspaces.value?.filter((p) => !modifiedIds.has(p.id)) ?? [];
	});

	function addWorkspace(workspace: MonospaceWorkspace) {
		if (!assignedWorkspaces.value.some((p) => p.id === workspace.id)) {
			assignedWorkspaces.value = [...assignedWorkspaces.value, workspace];
		}
	}

	function removeWorkspace(workspace: MonospaceWorkspace) {
		assignedWorkspaces.value = assignedWorkspaces.value.filter((p) => p.id !== workspace.id);
	}

	const originalIds = new Set(originalWorkspaces.map((p) => p.id));

	function isAddedWorkspace(workspace: MonospaceWorkspace) {
		return !originalIds.has(workspace.id);
	}

	// Compare against the set the dialog opened with (may include create-mode prefills), so an
	// untouched prefill does not read as a change.
	const hasWorkspaceChanges = computed(() =>
		xor(startingWorkspaces.map((p) => p.id), assignedWorkspaces.value.map((p) => p.id)).length > 0,
	);

	async function saveChanges({
		assignedWorkspaces: savedWorkspaces,
		rolesByWorkspaceId,
		serviceAccountId,
	}: {
		assignedWorkspaces: MonospaceWorkspace[];
		rolesByWorkspaceId?: Record<string, string[]>;
		serviceAccountId: string;
	}) {
		const modifiedIds = new Set(savedWorkspaces.map((p) => p.id));

		const addedWorkspaces = savedWorkspaces.filter((p) => !originalIds.has(p.id));
		const removedWorkspaces = originalWorkspaces.filter((p) => !modifiedIds.has(p.id));

		await Promise.all([
			...addedWorkspaces.map(async (workspace) => {
				await addServiceAccountToWorkspace(workspace.apiName, serviceAccountId, rolesByWorkspaceId?.[workspace.id] ?? []);
				store.MonospaceWorkspaceMember.writeItem({ userId: serviceAccountId, workspaceId: workspace.id });
			}),
			...removedWorkspaces.map(async (workspace) => {
				await removeServiceAccountFromWorkspace(workspace.apiName, serviceAccountId);
				store.MonospaceWorkspaceMember.clearItem(`${serviceAccountId}__${workspace.id}`);
			}),
		]);
	}

	return {
		availableWorkspaces,
		assignedWorkspaces,
		addWorkspace,
		removeWorkspace,
		isAddedWorkspace,
		hasWorkspaceChanges,
		saveChanges,
	};
}
